import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { formatDateTime } from "@/lib/datetime";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, MapPin, MailX } from "lucide-react";
import EnquirySourcesCard from "@/components/EnquirySourcesCard";

const FIELDS = [
  ["name", "Company / depot name"],
  ["line1", "Address line 1"],
  ["line2", "Address line 2"],
  ["city", "Town / city"],
  ["postcode", "Postcode"],
];

function EmailPauseCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/platform/email-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const toggle = async (paused) => {
    try {
      const { data } = await api.put("/platform/email-settings", { notifications_paused: paused });
      setS(data);
      toast.success(paused ? "Notification emails paused" : "Notification emails resumed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };
  if (!s) return null;
  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="email-pause-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><MailX className="w-4 h-4" /> Pause all notification emails</h2>
          <p className="mt-1 text-xs text-slate-500">Platform-wide, all companies. Stops automatic emails (approvals, reminders, automations, routes). Emails staff send on purpose — quotes, EPOS reports, test emails — still go out.</p>
          <p className={`mt-2 text-xs font-semibold ${s.notifications_paused ? "text-amber-600" : "text-emerald-600"}`} data-testid="email-pause-status">
            {s.notifications_paused ? "Paused — no automatic emails are being sent" : "Active — automatic emails are being sent"}
            {s.updated_by && <span className="font-normal text-slate-400"> · changed by {s.updated_by}{s.updated_at ? ` ${formatDateTime(s.updated_at)}` : ""}</span>}
          </p>
        </div>
        <Switch checked={s.notifications_paused} onCheckedChange={toggle} data-testid="email-pause-toggle" />
      </div>
    </section>
  );
}

export default function CompanySettings() {
  const { isPlatformAdmin } = useAuth();
  const [hq, setHq] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/company/hq").then((r) => setHq(r.data)).catch(() => setHq({})); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company/hq", hq);
      setHq(data);
      toast.success("HQ address saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (!hq) return null;
  return (
    <div className="p-4 sm:p-8 max-w-2xl" data-testid="company-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Company</h1>
          <p className="text-sm text-slate-500">HQ / depot address used for maps, routes and the Run Planner</p>
        </div>
      </div>
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-3" data-testid="company-hq-card">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><MapPin className="w-4 h-4" /> HQ / Depot address</h2>
        {!hq.configured && <p className="text-xs text-amber-600" data-testid="company-hq-not-set">Not set yet — using the app default.</p>}
        {FIELDS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-xs font-medium text-slate-600">{label}</span>
            <Input value={hq[k] || ""} onChange={(e) => setHq({ ...hq, [k]: e.target.value })} data-testid={`company-hq-${k}-input`} />
          </label>
        ))}
        <Button onClick={save} disabled={saving || !(hq.postcode || "").trim()} data-testid="company-hq-save-btn">
          {saving ? "Saving…" : "Save HQ address"}
        </Button>
      </section>
      <EnquirySourcesCard />
      {isPlatformAdmin && <EmailPauseCard />}
    </div>
  );
}
